import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./motion";
import { subscribe } from "../../lib/api";

export default function Contact({ profile }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe(email);
      toast.success("You're on the list. Thanks for subscribing.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const socials = profile?.socials || [];

  return (
    <section id="contact" data-testid="contact-section" className="relative mx-auto max-w-[1400px] px-6 md:px-10 py-28 md:py-40">
      <Reveal>
        <span className="font-mono text-xs uppercase tracking-[0.28em] text-[#7a7a7a]">/ 04 — Contact</span>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="font-serif text-3xl md:text-4xl text-[#8f8f8f] tracking-tight mb-4">Let&apos;s talk.</p>
            <a
              href={`mailto:${profile?.email}`}
              data-testid="contact-email-link"
              className="group block font-serif text-[10vw] lg:text-8xl tracking-tighter text-[#FAFAFA] leading-[0.9] break-words hover:italic transition-all duration-300"
            >
              {profile?.email}
              <ArrowUpRight className="inline-block ml-3 mb-4 opacity-40 group-hover:opacity-100 transition-opacity" size={40} />
            </a>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-x-10 gap-y-3 mt-10">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`social-${s.label.toLowerCase()}`}
                  className="font-mono text-xs uppercase tracking-[0.22em] text-[#a3a3a3] hover:text-[#FAFAFA] border-b border-transparent hover:border-[#FAFAFA] pb-1 transition-colors duration-300"
                >
                  {s.label} ↗
                </a>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-4">
          <Reveal delay={0.15}>
            <div className="border border-[#1c1c1c] p-6 bg-[#0a0a0a]">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7a7a7a] mb-2">Based in</div>
              <div className="font-serif text-2xl text-[#FAFAFA] mb-6">{profile?.location}</div>

              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7a7a7a] mb-2">Phone</div>
              <div className="text-[#c9c9c9] mb-6">{profile?.phone}</div>

              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7a7a7a] mb-3">
                Occasional letters
              </div>
              <form onSubmit={onSubscribe} className="flex flex-col gap-3" data-testid="subscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="subscribe-input"
                  className="bg-transparent border border-[#2a2a2a] px-4 py-3 text-sm text-[#FAFAFA] placeholder:text-[#5a5a5a] focus:border-[#FAFAFA] outline-none transition-colors"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="subscribe-btn"
                  className="bg-[#FAFAFA] text-[#050505] px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#d4d4d4] disabled:opacity-60 transition-colors"
                >
                  {loading ? "..." : "Subscribe"}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
